import { useState, useEffect } from 'react'
import useBodyScrollLock from '../../../../../hooks/useBodyScrollLock'
import { appToast } from '../../../../../components/AppToast'
import { getMasterOptions } from '../../api/mastersApi'
import { batchTransferStudents } from '../../api/studentsApi'

const RULES = [
  'Batch transfer option allows you to transfer all selected students from one batch to another batch.',
  'If you do not mark any entries for transfer, all students based on selected filter criteria would be transferred.',
  'To remove a batch, select the batch from dashboard, click on transfer and [Submit] without selecting target batch.',
  'If you generate batchwise Roll Nos., please make sure that there are no duplicate roll numbers in the target batch.',
]

export default function BatchTransferModal({
  isOpen,
  onClose,
  students = [],
  selectedStudentIds = new Set(),
  currentBatch,
  onComplete,
  onSuccess,
}) {
  const [targetBatch, setTargetBatch] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [batches, setBatches] = useState([])

  useBodyScrollLock(isOpen)

  useEffect(() => {
    if (!isOpen) return
    let active = true
    getMasterOptions('batches').then((opts) => {
      if (active) setBatches(opts || [])
    }).catch(() => {})
    return () => { active = false }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  useEffect(() => {
    if (isOpen) {
      setTargetBatch('')
      setIsProcessing(false)
    }
  }, [isOpen])

  const batchOptions = batches.filter((b) => b && b !== 'All').map((b) => ({ value: b, label: b }))

  const hasSelection = selectedStudentIds.size > 0
  const transferCount = hasSelection ? selectedStudentIds.size : students.length
  const transferLabel = hasSelection
    ? `Transferring ${transferCount} selected student${transferCount !== 1 ? 's' : ''}`
    : `Transferring all ${transferCount} filtered student${transferCount !== 1 ? 's' : ''}`

  const handleTransfer = async () => {
    if (!targetBatch) {
      appToast.warning('Please select a target batch.')
      return
    }
    if (targetBatch === currentBatch) {
      appToast.warning('Target batch cannot be the same as the source batch.')
      return
    }
    setIsProcessing(true)
    try {
      const studentIds = hasSelection
        ? Array.from(selectedStudentIds)
        : students.map((s) => s.id)
      const res = await batchTransferStudents({
        studentIds,
        targetBatch,
        currentBatch: currentBatch || undefined,
      })
      appToast.success(res?.message || `${transferLabel} to ${targetBatch}.`)
      onSuccess?.()
      onComplete?.()
      onClose()
    } catch (err) {
      appToast.error(err?.response?.data?.detail || 'Failed to transfer students.')
    } finally {
      setIsProcessing(false)
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/45 p-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[560px] flex-col overflow-hidden rounded-2xl bg-surface-container-lowest shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header Banner ────────────────────────────────────────────── */}
        <div className="flex items-center gap-3 bg-[#0088B2] px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/80 transition-colors hover:bg-white/15 hover:text-white"
          >
            <span className="material-symbols-outlined text-xl">undo</span>
          </button>
          <div>
            <h3 className="text-lg font-bold text-white">Batch Transfer</h3>
            <p className="text-xs text-white/70">
              {transferLabel}
            </p>
          </div>
        </div>

        {/* ── Body ──────────────────────────────────────────────────────── */}
        <div className="overflow-y-auto px-6 py-5">
          {/* Rules Card */}
          <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50/50 p-4">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined mt-0.5 shrink-0 text-lg text-[#0088B2]">info</span>
              <div className="space-y-2">
                {RULES.map((rule, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs leading-relaxed text-slate-700">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#0088B2]/60" />
                    <span>{rule}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Target Batch Form */}
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <label className="mb-2 block text-sm font-semibold text-slate-800">
              Select Target Batch
            </label>
            <select
              value={targetBatch}
              onChange={(e) => setTargetBatch(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm transition-all placeholder:text-slate-400 focus:border-[#0088B2] focus:outline-none focus:ring-2 focus:ring-[#0088B2]/20"
            >
              <option value="">Select</option>
              {batchOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Scope pill */}
            <div className="mt-4 flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                  hasSelection
                    ? 'bg-primary/10 text-primary'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                <span className="material-symbols-outlined text-sm">
                  {hasSelection ? 'check_box' : 'filter_list'}
                </span>
                {transferLabel}
              </span>
              {currentBatch && currentBatch !== 'All' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">
                  From: {currentBatch}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 transition-colors hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleTransfer}
            disabled={isProcessing || !targetBatch}
            className="rounded-lg bg-[#0088B2] px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isProcessing ? (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined animate-spin text-base">progress_activity</span>
                Transferring...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-base">swap_horiz</span>
                Submit
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
